import React, { useCallback } from "react";
import useDialog from "../../hooks/useDialog";
import { GraphStructure, SplitData, Workflow } from "./WorkflowsBuilder/types";
import { ElkNode } from "elkjs";
import elkLayout from "./WorkflowsBuilder/graph";
import { getIdFromUrl } from "./WorkflowBuilderPage.utils";
import {
  areAllNodesConfigured,
  atleastOneActionNodePresent,
  getInvalidSplitNodes,
  isLogicNodePresentInAssignToAudience,
  isMoreThanNConditionalNode,
  isSubsequentActionPreset,
  isSubsequentDelayPreset,
  isTimerAfterActionNode,
} from "./WorkflowsBuilder/workflowValidator";
import {
  useGetWorkflowByIdQuery,
  useGetWorkflowPublishImpactByIdQuery,
  usePostWorkflowPublishByIdMutation,
  usePutWorkflowActivateByIdMutation,
  usePutWorkflowByIdMutation,
  usePutWorkflowPauseByIdMutation,
  useGetWorkflowStatsByIdQuery,
} from "../../app/useGeneratedApi";
import {
  WorkflowPublishImpactView,
  WorkflowPublishResponseView,
} from "../../app/GeneratedApi";
import { useConfigView } from "../../Config.context";
import {
  hasErrorCode,
  WORKFLOW_DEFINITION_INVALID_CODE,
  WORKFLOW_DRAFT_CHANGED_CODE,
} from "../../utils/errorUtils";
import { api } from "../../app/apiInstance";
import {
  getLifetimeSwitchWarning,
  getSplitRestartWarning,
  splitClearsWinner,
  splitRestartsTest,
} from "./WorkflowsBuilder/Split/split.helpers";
import { closeSnackbar, enqueueSnackbar, SnackbarKey } from "notistack";
import { Button } from "@mui/material";
import { useDialogInvokers } from "@tiny/lib";
import WorkflowEditConfirmationDialogContent from "../../customComponents/WorkflowEditPausedDialog";
import WorkflowPublishConfirmationDialogContent from "../../customComponents/WorkflowPublishDialog";
import {
  buildMoveWaitingRetryRequest,
  buildPublishRequest,
  isMoveWaitingFailure,
} from "../../customComponents/WorkflowPublishDialog/publishDialog.helpers";
import useJwtToken from "../../hooks/useJwtToken";

export const useWorkflowsBuilderPage = () => {
  const workflowId = getIdFromUrl();
  const { config } = useConfigView();
  const { quitApp = () => {}, navigateTo = () => {} } = config || {};
  const token = useJwtToken();

  const { requestSmallDialog, exitDialog } = useDialogInvokers();

  const {
    data: workflowDataResponse,
    isLoading: isWorkflowDataResponseLoading,
    refetch: refetchWorkflow,
    error: getWorkflowError,
  } = useGetWorkflowByIdQuery(workflowId);

  const {
    data: workflowStatsResponse,
    isLoading: isWorkflowStatsLoading,
    refetch: refetchWorkflowStats,
    error: getWorkflowStatsError,
  } = useGetWorkflowStatsByIdQuery(workflowId, {
    enabled: !!(
      workflowDataResponse &&
      (workflowDataResponse.status == "PAUSED" ||
        workflowDataResponse.status == "ACTIVE")
    ),
  });

  const {
    mutateAsync: updateWorkflowMutate,
    isLoading: isUpdateWorkflowLoading,
  } = usePutWorkflowByIdMutation();

  const { refetch: fetchPublishImpact } = useGetWorkflowPublishImpactByIdQuery(
    workflowId,
    { enabled: false }
  );

  const { mutateAsync: publishWorkflowMutate } =
    usePostWorkflowPublishByIdMutation();

  const isLoading = isWorkflowDataResponseLoading || isUpdateWorkflowLoading;

  const [workflowData, setWorkflowData] = React.useState<Workflow>({
    structure: { nodes: [], edges: [] },
    id: "",
    name: "",
    description: "",
    template_id: "",
    status: "DRAFT",
  });

  const [currentNodeId, setCurrentNodeId] = React.useState<
    string | undefined
  >();
  const clearCurrentNodeId = () => setCurrentNodeId(undefined);

  const [downloadEnabled, setDownloadEnabled] = React.useState(false);
  const [viewMode, setViewMode] = React.useState(false);
  const [hasUnpublishedChanges, setHasUnpublishedChanges] =
    React.useState(false);
  const [isPublishImpactLoading, setIsPublishImpactLoading] =
    React.useState(false);

  /**
   * Serialized copy of the workflow as the server last saw it - either as it
   * came back from the GET, or as we last PUT it. The autosave effect compares
   * against this so that loading a workflow (or re-rendering with unchanged
   * data) never PUTs it straight back.
   */
  const lastPersistedRef = React.useRef<string | null>(null);
  const serializeWorkflow = (data: Workflow) => JSON.stringify(data);

  /**
   * The autosave PUT currently in flight, if any. The autosave effect fires and
   * forgets, so Publish has no other way to tell that the server's draft is
   * still catching up with the canvas - and publishing then promotes a revision
   * the impact report the customer just approved never described.
   */
  const pendingSaveRef = React.useRef<Promise<void> | null>(null);

  /** The live draft, readable from effects that must not re-run on every edit. */
  const workflowDataRef = React.useRef(workflowData);
  workflowDataRef.current = workflowData;
  const [campaignBuilderOpen, setCampaignBuilderOpen] = React.useState(false);
  const [smsBuilderOpen, setSmsBuilderOpen] = React.useState(false);

  /**
   * Unsaved settings of the split whose panel is open, so its canvas labels
   * follow the form while the customer types. Never saved.
   */
  const [splitPreview, setSplitPreview] = React.useState<{
    nodeId: string;
    data: SplitData;
  } | null>(null);
  const {
    mutateAsync: activateWorkflowsMutate,
    isLoading: activateWorkflowsLoading,
  } = usePutWorkflowActivateByIdMutation();

  const {
    mutateAsync: pauseWorkflowsMutate,
    isLoading: pauseWorkflowsLoading,
  } = usePutWorkflowPauseByIdMutation();

  const pauseScheduledWorkflow = React.useCallback(async (id: string) => {
    try {
      await pauseWorkflowsMutate({ id });
      setWorkflowData((prev) => ({
        ...prev,
        status: "PAUSED",
      }));
      setViewMode(false);
      exitDialog();
    } catch (error) {}
  }, []);

  const activateWorkflow = React.useCallback(async (id: string) => {
    try {
      await activateWorkflowsMutate({ id });
      setWorkflowData((prev) => ({
        ...prev,
        status: "ACTIVE",
      }));
      navigateTo("/main/workflows");
    } catch (error) {}
  }, []);

  const quitWithoutSaving = () => {
    if (quitApp) {
      quitApp();
    }
  };
  const {
    dialogVisible: quitWorkflowsVisible,
    hideDialog: hideQuitWorkflows,
    showDialog: showQuitWorkflows,
  } = useDialog();

  //Component functions
  const saveWorkflow = (data: Workflow) => {
    const snapshot = serializeWorkflow(data);
    lastPersistedRef.current = snapshot;
    const save = (async () => {
      try {
        await updateWorkflowMutate({ id: workflowId, data });
        // PUT /workflow/{id} answers 204 with no body, so the draft-vs-published
        // comparison cannot come from here. Assume the edit left the draft ahead
        // of the published revision: GET /workflow/{id} carries the
        // authoritative flag, and POST /publish writes nothing and answers
        // changed:false if it turns out there was nothing to publish.
        setHasUnpublishedChanges(true);
      } catch (error) {
        // Let the next change retry the save instead of treating it as persisted.
        if (lastPersistedRef.current === snapshot) {
          lastPersistedRef.current = null;
        }
      }
    })();
    pendingSaveRef.current = save;
    void save.then(() => {
      if (pendingSaveRef.current === save) {
        pendingSaveRef.current = null;
      }
    });
    return save;
  };

  const openCampaignBuilder = (id: string) => {
    setCurrentNodeId(id);
    setCampaignBuilderOpen(true);
  };

  const closeCampaignBuilder = () => {
    clearCurrentNodeId();
    setCampaignBuilderOpen(false);
  };

  /**
   * The new revision is live but the contacts already waiting kept their old
   * times. Retry resends the same nodes on a publish that changes nothing,
   * which the backend runs as the move against the revision in force.
   */
  const warnMoveWaitingFailed = (
    reviewed: WorkflowPublishImpactView | undefined,
    published: WorkflowPublishResponseView,
    moveWaitingNodeIds: string[]
  ) => {
    const retry = async (key: SnackbarKey) => {
      closeSnackbar(key);
      try {
        const retried = await publishWorkflowMutate({
          id: workflowId,
          data: buildMoveWaitingRetryRequest(
            reviewed,
            published,
            moveWaitingNodeIds
          ),
        });
        if (isMoveWaitingFailure(retried)) {
          warnMoveWaitingFailed(reviewed, published, moveWaitingNodeIds);
        } else {
          enqueueSnackbar("Contacts already waiting are being moved.", {
            variant: "success",
            anchorOrigin: {
              vertical: "top",
              horizontal: "center",
            },
            autoHideDuration: 5000,
          });
        }
      } catch (error) {
        if (hasErrorCode(error, WORKFLOW_DRAFT_CHANGED_CODE)) {
          enqueueSnackbar(
            "This workflow changed since it was published, so contacts already waiting were not moved.",
            {
              variant: "warning",
              anchorOrigin: {
                vertical: "top",
                horizontal: "center",
              },
              autoHideDuration: 10000,
            }
          );
        } else {
          // Anything else (a 500, a timeout, no connection) can be retried, and
          // the warning was closed before the request - raise it again so
          // Retry stays reachable instead of the move failing silently.
          warnMoveWaitingFailed(reviewed, published, moveWaitingNodeIds);
        }
      }
    };
    enqueueSnackbar(
      "Published, but contacts already waiting could not be moved.",
      {
        variant: "warning",
        anchorOrigin: {
          vertical: "top",
          horizontal: "center",
        },
        persist: true,
        action: (key) => (
          <>
            <Button color="inherit" size="small" onClick={() => retry(key)}>
              Retry
            </Button>
            <Button
              color="inherit"
              size="small"
              onClick={() => closeSnackbar(key)}
            >
              Dismiss
            </Button>
          </>
        ),
      }
    );
  };

  const publishWorkflow = async (
    reviewed?: WorkflowPublishImpactView,
    moveWaitingNodeIds: string[] = []
  ) => {
    try {
      // The hash and revision the customer just reviewed. The backend compares
      // them against the draft it is about to publish and refuses a mismatch
      // (412, code 9.0.009), so an autosave that landed after the report was
      // built cannot be published behind the customer's back.
      const published = await publishWorkflowMutate({
        id: workflowId,
        data: buildPublishRequest(reviewed, moveWaitingNodeIds),
      });
      setHasUnpublishedChanges(false);
      exitDialog();
      if (isMoveWaitingFailure(published)) {
        warnMoveWaitingFailed(reviewed, published, moveWaitingNodeIds);
      } else {
        enqueueSnackbar("Workflow published. Your changes are now live.", {
          variant: "success",
          anchorOrigin: {
            vertical: "top",
            horizontal: "center",
          },
          autoHideDuration: 5000,
        });
      }
      refetchWorkflow();
    } catch (error) {
      if (hasErrorCode(error, WORKFLOW_DRAFT_CHANGED_CODE)) {
        exitDialog();
        enqueueSnackbar(
          "This workflow changed while you were reviewing it. Nothing was published - open Publish again to see the current changes.",
          {
            variant: "warning",
            anchorOrigin: {
              vertical: "top",
              horizontal: "center",
            },
            autoHideDuration: 10000,
          }
        );
        refetchWorkflow();
      }
    }
  };

  /**
   * Splits whose published test this publish would restart, or whose winner a
   * switch to a lifetime split would clear. The split stats
   * endpoint describes the published split, so comparing the draft against it
   * is the same comparison the backend makes when the publish lands.
   */
  const getSplitRestartWarnings = async (): Promise<string[]> => {
    const current = workflowDataRef.current;
    if (current.status !== "ACTIVE") {
      return [];
    }
    const splits = current.structure.nodes.filter(
      (node) => node.type === "SPLIT"
    );
    const warnings = await Promise.all(
      splits.map(async (node) => {
        try {
          const { data: published } = await api.getWorkflowSplitStatsById(
            workflowId,
            node.id
          );
          if (
            splitRestartsTest(node.data, {
              goal: published.goal,
              branchIds: published.branches.map((branch) => branch.branchId),
            })
          ) {
            return getSplitRestartWarning(node.data.label);
          }
          return splitClearsWinner(node.data, {
            hasWinner: !!published.winner,
          })
            ? getLifetimeSwitchWarning(node.data.label)
            : null;
        } catch (error) {
          // 412: not a split in the published workflow yet, so no test to restart.
          return null;
        }
      })
    );
    return warnings.filter((warning): warning is string => !!warning);
  };

  const showPublishConfirmation = async () => {
    setIsPublishImpactLoading(true);
    try {
      // The autosave effect fires its PUT and forgets it. Publishing while one
      // is in flight would promote a draft this report never saw, so wait for it
      // before asking the server what publishing would do.
      await pendingSaveRef.current;
      const {
        data: impact,
        isError,
        error: impactError,
      } = await fetchPublishImpact();
      if (
        isError &&
        hasErrorCode(impactError, WORKFLOW_DEFINITION_INVALID_CODE)
      ) {
        // The query's onError already shows the backend's message, e.g.
        // "Split node is invalid: branch percentages must add up to 100".
        return;
      }
      // react-query keeps the previous `data` when a refetch fails, so `data`
      // alone cannot say whether THIS fetch succeeded - opening the dialog on it
      // would describe a report that may be several edits old.
      if (isError || !impact) {
        enqueueSnackbar(
          "Could not work out what publishing would change. Please try again.",
          {
            variant: "error",
            anchorOrigin: {
              vertical: "top",
              horizontal: "center",
            },
            autoHideDuration: 10000,
          }
        );
        return;
      }
      const splitWarnings = impact.hasChanges
        ? await getSplitRestartWarnings()
        : [];
      requestSmallDialog({
        titleProps: { children: "Publish changes", variant: "label1R" },
        contentProps: {
          dividers: false,
          sx: {
            border: "none",
          },
          children: (
            <WorkflowPublishConfirmationDialogContent
              impact={impact}
              splitWarnings={splitWarnings}
              onPublish={(moveWaitingNodeIds) =>
                publishWorkflow(impact, moveWaitingNodeIds)
              }
              onCancel={exitDialog}
            />
          ),
        },
      });
    } finally {
      setIsPublishImpactLoading(false);
    }
  };

  const openSmsBuilder = (id: string) => {
    setCurrentNodeId(id);
    setSmsBuilderOpen(true);
  };

  const closeSmsBuilder = () => {
    clearCurrentNodeId();
    setSmsBuilderOpen(false);
  };

  const showPauseConfirmation = (id: string) => {
    requestSmallDialog({
      titleProps: { children: "Please confirm", variant: "label1R" },
      contentProps: {
        dividers: false,
        sx: {
          border: "none",
        },
        children: (
          <WorkflowEditConfirmationDialogContent
            onContinue={() => pauseScheduledWorkflow(id)}
            onCancel={exitDialog}
          />
        ),
      },
    });
  };

  const getHeaderControlAction = () => {
    return {
      disabled: false,
      type: "text",
      text: "Actions",
      onClick: () => {},
      children: [
        {
          text: "Activate",
          onClick: () => activateWorkflow(workflowId),
          hidden: false,
          disabled:
            workflowData.status === "ACTIVE" ||
            !getRestrictionOptions().every((n) => n.isValid),
        },
        {
          text: "Publish",
          onClick: () => showPublishConfirmation(),
          hidden: workflowData.status !== "ACTIVE",
          disabled:
            !hasUnpublishedChanges ||
            isPublishImpactLoading ||
            isUpdateWorkflowLoading ||
            getInvalidSplitNodes(workflowData.structure).length > 0,
        },
        {
          text: "Pause",
          onClick: () => showPauseConfirmation(workflowId),
          hidden: false,
          disabled: workflowData.status !== "ACTIVE",
        },
      ],
    };
  };

  // Macro Workflow Builder functions

  const setGraphData = (graphData: GraphStructure) => {
    elkLayout(graphData.nodes, graphData.edges)
      .then((graph: any) => {
        const nodesForFlow =
          graph?.children?.map((node: ElkNode) => ({
            ...graphData.nodes.find((n) => n.id === node.id),
            position: { x: node.x, y: node.y },
          })) || [];

        const updatedGraph = {
          ...workflowData,
          structure: {
            nodes: nodesForFlow,
            edges: graph.edges,
          },
        };
        setWorkflowData(updatedGraph);
      })
      .catch((error) => {
        console.error("Error updating graph:", error);
      });
  };

  const triggers = [
    {
      title: "Rules",
      subtitle: "Set your own rules for trigger.",
      active: true,
      onclick: () => {
        setWorkflowData((prev) => ({
          ...prev,
          triggerType: "RULE",
        }));
      },
    },
    {
      title: "API",
      subtitle: "Trigger a workflow based on an API",
      active: true,
      onclick: () => {
        setWorkflowData((prev) => ({
          ...prev,
          triggerType: "API",
        }));
      },
    },
  ];

  const updateNode = ({
    id,
    data,
    updateLabel = false,
  }: {
    id: string;
    data: any;
    updateLabel?: boolean;
  }) => {
    const updatedNodes = [...workflowData.structure.nodes];
    const index = workflowData.structure?.nodes?.findIndex(
      (node) => node.id === id
    );
    updatedNodes[index] = {
      ...updatedNodes[index],
      data: {
        ...updatedNodes[index].data,
        ...data,
        ...(!updateLabel && { label: updatedNodes[index].data.label }),
      },
    };

    if (index >= 0) {
      setWorkflowData((prev) => ({
        ...prev,
        structure: {
          ...prev.structure,
          nodes: updatedNodes,
        },
      }));
    } else {
      console.log("error saving");
    }
  };

  const getNode = (nodeId: string) => {
    return workflowData.structure?.nodes?.find((node) => node.id === nodeId);
  };

  const getRestrictionOptions = useCallback(() => {
    const atleastOneActionNodePresentRule = atleastOneActionNodePresent(
      workflowData.structure
    );
    const { isValid, notConfiguredNodes } = areAllNodesConfigured(
      workflowData.structure
    );
    const isSubsequentDelayPresetRule = isSubsequentDelayPreset(
      workflowData.structure
    );

    const isSubsequentActionPresetRule = isSubsequentActionPreset(
      workflowData.structure
    );

    const isMoreThanTwentyFiveConditionalNodeRule = isMoreThanNConditionalNode(
      workflowData.structure,
      25
    );

    const isTimerAfterActionRule = isTimerAfterActionNode(
      workflowData.structure
    );

    const isLogicNodePresentInAssignToAudienceRule =
      isLogicNodePresentInAssignToAudience(workflowData.structure);

    const invalidSplitNodes = getInvalidSplitNodes(workflowData.structure);

    return [
      {
        label: `Node(s) [ ${notConfiguredNodes
          ?.map((node) => node.type)
          .join(", ")} ] are not configured!`,
        isValid: isValid,
      },
      {
        label: "Each flow must end with atleast one ACTION node",
        isValid: atleastOneActionNodePresentRule,
      },
      {
        label: "Subsequent TIMING nodes cannot be applied.",
        isValid: isSubsequentDelayPresetRule,
      },
      {
        label: "Subsequent action nodes of the same type cannot be applied.",
        isValid: isSubsequentActionPresetRule,
      },
      {
        label:
          "Only Twenty Five LOGIC ( Conditional ) Nodes can be present in a workflow.",
        isValid: isMoreThanTwentyFiveConditionalNodeRule,
      },
      {
        label: "A Timer Node is required between two Action Nodes",
        isValid: isTimerAfterActionRule,
      },
      {
        label: `Traffic Split node(s) [ ${invalidSplitNodes
          .map((node) => node.data.label)
          .join(
            ", "
          )} ] need valid paths, percentages adding up to 100% and a goal.`,
        isValid: invalidSplitNodes.length === 0,
      },
    ];
  }, [workflowData]);

  React.useEffect(() => {
    if (!workflowDataResponse) {
      return;
    }
    // refetchWorkflow() after publish is fired and forgotten, and the customer
    // can keep editing while the GET is in flight. `{...prev, ...response}`
    // would drop that edit, and resetting lastPersistedRef would stop the
    // autosave effect below ever noticing it was lost - so a dirty canvas takes
    // only the fields the server owns.
    const local = workflowDataRef.current;
    const hasLocalEdits =
      pendingSaveRef.current !== null ||
      (!!local.id &&
        lastPersistedRef.current !== null &&
        serializeWorkflow(local) !== lastPersistedRef.current);

    if (hasLocalEdits) {
      setWorkflowData((prevState) => ({
        ...prevState,
        status: workflowDataResponse.status,
      }));
    } else {
      setWorkflowData((prevState) => {
        const loaded = { ...prevState, ...workflowDataResponse };
        // What we just loaded is by definition what the server already has.
        lastPersistedRef.current = serializeWorkflow(loaded);
        return loaded;
      });
    }

    // Only ACTIVE workflows carry the flag - it is null otherwise - and it
    // describes the draft as the server had it, so an edit still waiting to be
    // saved is unpublished whatever the server said.
    if (workflowDataResponse.hasUnpublishedChanges != null) {
      setHasUnpublishedChanges(
        workflowDataResponse.hasUnpublishedChanges || hasLocalEdits
      );
    }

    if (workflowDataResponse.status !== "DRAFT") {
      setDownloadEnabled(true);
    }
  }, [workflowDataResponse]);

  React.useEffect(() => {
    if (!workflowData.id) {
      return;
    }
    if (serializeWorkflow(workflowData) === lastPersistedRef.current) {
      return;
    }
    saveWorkflow(workflowData);
  }, [workflowData]);

  React.useEffect(() => {
    if (getWorkflowError?.message) {
      enqueueSnackbar(`Error Fetching workflow with id ${workflowId}.`, {
        variant: "error",
        anchorOrigin: {
          vertical: "top",
          horizontal: "center",
        },
        autoHideDuration: 10000,
      });
    }
  }, [getWorkflowError]);

  const goBackToWorkflowPage = () => {
    navigateTo("/main/workflows");
  };

  const showNodeStats =
    workflowData?.status === "PAUSED" || workflowData?.status === "ACTIVE";

  return {
    workflowId,
    currentNodeId,
    campaignBuilderOpen,
    closeCampaignBuilder,
    smsBuilderOpen,
    openSmsBuilder,
    closeSmsBuilder,
    downloadEnabled,
    getHeaderControlAction,
    hideQuitWorkflows,
    openCampaignBuilder,
    quitWithoutSaving,
    quitWorkflowsVisible,
    saveWorkflow,
    showQuitWorkflows,
    setGraphData,
    workflowData,
    setWorkflowData,
    refetchWorkflow,
    triggers,
    isLoading,
    updateNode,
    setViewMode,
    viewMode,
    getNode,
    getRestrictionOptions,
    hasUnpublishedChanges,
    isPublishImpactLoading,
    isSavingWorkflow: isUpdateWorkflowLoading,
    showPublishConfirmation,
    goBackToWorkflowPage,
    workflowStats: workflowStatsResponse,
    isStatsLoading: isWorkflowStatsLoading,
    refetchWorkflowStats: refetchWorkflowStats,
    getWorkflowStatsError,
    showNodeStats,
    splitPreview,
    setSplitPreview,
    token,
  };
};
